// src/private/src/pages/app/clients/components/PeerFunnelComparison.jsx
import React, { memo, useMemo } from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';

// ─── Funnel stages ────────────────────────────────────────────────────────────
// Compound percentage of LEADS that reach each stage. The bars decrease in
// width naturally (set_rate × issue_rate × demo_rate × net_close_rate
// produces monotonically smaller numbers) so we get the classic funnel
// shape while keeping every number on the chart as a rate, not a volume.

const FUNNEL_STAGES = [
  { key: 'booked',  label: 'Booked',    color: '#7c3aed' },
  { key: 'issues',  label: 'Issues',    color: '#f59e0b' },
  { key: 'demos',   label: 'Demos',     color: '#f97316' },
  { key: 'netSold', label: 'Net Sold',  color: '#22c55e' },
];

// Conversion rate between adjacent stages. Used in the row between each pair
// of bars. The "to" stage is the destination; the rate is the % that converts
// from previous → next stage.
//
// compoundKey is the lead-to-X version of the same stage (same denominator at
// every stage). The "biggest opportunity" callout and AI narrative diagnose
// using compoundKey, not rateKey, because stage rates are corrupted by
// cross-client definitional differences (one client books loosely → high set
// rate / low issue rate; another books tight → opposite — but both can have
// the same lead-to-issue).
const STAGE_TRANSITIONS = [
  { fromKey: 'leads',  toKey: 'booked',  label: 'Set Rate',    rateKey: 'set_rate',       compoundKey: 'lead_to_booked_rate', compoundLabel: 'Lead-to-Booked' },
  { fromKey: 'booked', toKey: 'issues',  label: 'Issue Rate',  rateKey: 'issue_rate',     compoundKey: 'lead_to_issue_rate',  compoundLabel: 'Lead-to-Issue' },
  { fromKey: 'issues', toKey: 'demos',   label: 'Demo Rate',   rateKey: 'demo_rate',      compoundKey: 'lead_to_demo_rate',   compoundLabel: 'Lead-to-Demo' },
  { fromKey: 'demos',  toKey: 'netSold', label: 'Close Rate',  rateKey: 'net_close_rate', compoundKey: 'lead_to_sale_rate',   compoundLabel: 'Lead-to-Sale' },
];

const BOTTOM_TILES = [
  { key: 'cost_of_marketing',  label: 'Cost of Marketing',icon: '💸', format: 'pct', goodDirection: 'lower', showTopQuartile: true },
  { key: 'revenue_per_lead',   label: 'Revenue per Lead', icon: '💵', format: 'money' },
  { key: 'avg_ticket',         label: 'Avg Ticket',       icon: '🏷️', format: 'money' },
  { key: 'self_book_rate',     label: 'Self-Book Rate',   icon: '📅', format: 'pct' },
];

// ─── Formatters ───────────────────────────────────────────────────────────────

const fmtPct = (v, digits = 1) => (Number.isFinite(Number(v))
  ? `${(Number(v) * 100).toFixed(digits)}%`
  : '—');
const fmtInt = (v) => Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtMoney = (v) => (Number.isFinite(Number(v))
  ? `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
  : '—');
const fmtMoneyShort = (v) => {
  if (!Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
};

const safeDiv = (a, b) => (Number(b) > 0 ? Number(a) / Number(b) : null);

// ─── Statistical helpers ──────────────────────────────────────────────────────
// Small-volume clients have high variance in their funnel rates. A client
// with 100 leads and 18 bookings (18%) has a Wilson 95% CI of roughly 11%-27%
// — calling them "underperforming" relative to a 20.5% vertical avg is noise,
// not a real gap. We use these helpers to compute the CI on each rate and
// only mark deltas as wins/losses when they exceed statistical variation.

const Z_95 = 1.96;

function wilsonInterval(successes, trials) {
  const n = Number(trials);
  const k = Number(successes);
  if (!Number.isFinite(n) || !Number.isFinite(k) || n <= 0) return null;
  const p = k / n;
  const z2 = Z_95 * Z_95;
  const denom = 1 + z2 / n;
  const center = (p + z2 / (2 * n)) / denom;
  const margin = (Z_95 * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n))) / denom;
  return {
    p,
    n,
    lower: Math.max(0, center - margin),
    upper: Math.min(1, center + margin),
    margin, // half-width of CI in raw rate units
  };
}

// Is the peer rate within the client's confidence band? If so, the apparent
// delta is consistent with sampling noise and we should NOT treat it as a
// meaningful win/loss.
function isSignificantGap(clientCI, peerRate) {
  if (!clientCI || !Number.isFinite(Number(peerRate))) return false;
  const p = Number(peerRate);
  return p < clientCI.lower || p > clientCI.upper;
}

// ─── Delta pill ───────────────────────────────────────────────────────────────

function DeltaPill({ delta, format = 'pt', goodDirection = 'higher', sx, deadband = 0 }) {
  if (!Number.isFinite(delta) || Math.abs(delta) <= deadband) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.3} sx={{ color: 'text.disabled', ...sx }}>
        <RemoveIcon sx={{ fontSize: 12 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.68rem' }}>even</Typography>
      </Stack>
    );
  }
  const isGood = goodDirection === 'higher' ? delta > 0 : delta < 0;
  const color = isGood ? '#16a34a' : '#dc2626';
  const Icon = delta > 0 ? TrendingUpIcon : TrendingDownIcon;
  let text;
  if (format === 'pt') text = `${delta > 0 ? '+' : ''}${(delta * 100).toFixed(1)}pt`;
  else if (format === 'dollar') text = `${delta > 0 ? '+' : '−'}$${Math.abs(Math.round(delta)).toLocaleString('en-US')}`;
  else text = `${delta > 0 ? '+' : ''}${Math.round(delta).toLocaleString('en-US')}`;
  return (
    <Stack direction="row" alignItems="center" spacing={0.3} sx={{ color, ...sx }}>
      <Icon sx={{ fontSize: 13 }} />
      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>{text}</Typography>
    </Stack>
  );
}

// ─── Half-funnel bar (back-to-back) ───────────────────────────────────────────

function HalfBar({ pctOfLeads, ciLower, ciUpper, scaleMax, color, side, dim, label, tooltipTitle }) {
  // Bar width is proportional to compound % of leads reaching this stage,
  // rescaled so the largest stage in the funnel uses the full half-width.
  // Without this rescaling small funnels (e.g., booked=28%) look stranded in
  // empty space; with it, the funnel always fills the available width while
  // still narrowing stage-over-stage.
  const widthPct = Number.isFinite(pctOfLeads) && scaleMax > 0
    ? Math.max((pctOfLeads / scaleMax) * 100, 6)
    : 6;
  const lowerWidth = Number.isFinite(ciLower) && scaleMax > 0
    ? Math.max((ciLower / scaleMax) * 100, 0)
    : null;
  const upperWidth = Number.isFinite(ciUpper) && scaleMax > 0
    ? Math.min((ciUpper / scaleMax) * 100, 100)
    : null;
  // The CI overlay is drawn behind the solid bar so it appears as a faded
  // "wiggle zone" extending past the bar's confident edge — and recessing
  // slightly in the other direction, since the true value could be lower too.
  const showCIBand = lowerWidth !== null && upperWidth !== null && upperWidth > lowerWidth;
  const radius = side === 'left' ? '6px 0 0 6px' : '0 6px 6px 0';
  const inner = (
    <Box sx={{ position: 'relative', display: 'flex', justifyContent: side === 'left' ? 'flex-end' : 'flex-start', width: '100%', cursor: tooltipTitle ? 'help' : 'default' }}>
      {showCIBand && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 2,
            bottom: 2,
            ...(side === 'left'
              ? { right: 0, width: `${upperWidth}%` }
              : { left: 0, width: `${upperWidth}%` }),
            display: 'flex',
            justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              width: `${((upperWidth - lowerWidth) / upperWidth) * 100}%`,
              height: '100%',
              borderRadius: 1,
              background: `repeating-linear-gradient(${side === 'left' ? '45deg' : '-45deg'}, ${color}40 0, ${color}40 5px, ${color}1f 5px, ${color}1f 10px)`,
              border: `1.5px dashed ${color}99`,
              opacity: dim ? 0.6 : 0.9,
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          position: 'relative',
          width: `${widthPct}%`,
          minWidth: 64,
          height: 34,
          borderRadius: radius,
          background: dim
            ? `linear-gradient(${side === 'left' ? '90deg' : '270deg'}, ${color}66, ${color}33)`
            : `linear-gradient(${side === 'left' ? '90deg' : '270deg'}, ${color}, ${color}dd)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
          px: 1.25,
          color: dim ? color : '#fff',
          fontWeight: 800,
          fontSize: '0.84rem',
          letterSpacing: '0.02em',
          boxShadow: dim ? 'none' : `0 4px 12px ${color}33`,
          transition: 'width 500ms cubic-bezier(.2,.7,.3,1)',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
    </Box>
  );
  return tooltipTitle ? <Tooltip arrow placement="top" title={tooltipTitle}>{inner}</Tooltip> : inner;
}

// ─── Bottom summary tile ──────────────────────────────────────────────────────

function StatTile({ label, icon, youValue, peerValue, topQuartileValue, format, goodDirection = 'higher', tooltipBody }) {
  const formatVal = (v) => {
    if (!Number.isFinite(Number(v))) return '—';
    if (format === 'money') return fmtMoney(v);
    if (format === 'pct') return fmtPct(v);
    return fmtInt(v);
  };
  const delta = (Number.isFinite(Number(youValue)) && Number.isFinite(Number(peerValue)))
    ? Number(youValue) - Number(peerValue)
    : null;
  const deltaFormat = format === 'money' ? 'dollar' : format === 'pct' ? 'pt' : 'abs';
  const hasTopQ = Number.isFinite(Number(topQuartileValue));
  const tile = (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.paper', borderColor: 'rgba(99, 102, 241, 0.12)', cursor: tooltipBody ? 'help' : 'default' }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <Box component="span" sx={{ fontSize: '0.9rem' }}>{icon}</Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.65rem' }}
        >
          {label}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.25 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#4f46e5' }}>
          {formatVal(youValue)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
          vs {formatVal(peerValue)}
        </Typography>
      </Stack>
      {hasTopQ && (
        <Typography variant="caption" sx={{ display: 'block', color: '#16a34a', fontSize: '0.66rem', fontWeight: 600, mb: 0.25 }}>
          Top quartile: {formatVal(topQuartileValue)}
        </Typography>
      )}
      {delta !== null && <DeltaPill delta={delta} format={deltaFormat} goodDirection={goodDirection} />}
    </Paper>
  );
  return tooltipBody ? <Tooltip arrow placement="top" title={tooltipBody}>{tile}</Tooltip> : tile;
}

// ─── Main component ──────────────────────────────────────────────────────────

// Tiny inline SVG sparkline: two lines (client + vertical avg) over recent
// months. Designed to live next to the rate value in the funnel so it answers
// "am I trending up or down vs peers" at a glance.
function MiniSparkline({ clientSeries, peerSeries, color = '#7c3aed', peerColor = '#94a3b8', width = 90, height = 22 }) {
  const padX = 2;
  const padY = 3;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const series = [
    { values: peerSeries, stroke: peerColor, opacity: 0.65, strokeWidth: 1.2, dashed: true },
    { values: clientSeries, stroke: color, opacity: 1, strokeWidth: 1.6, dashed: false },
  ];
  // Find combined min/max across both series for shared y-scale.
  const allValues = series.flatMap((s) => (s.values || []).filter((v) => Number.isFinite(Number(v))).map(Number));
  if (allValues.length < 2) {
    return (
      <Box sx={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>—</Typography>
      </Box>
    );
  }
  const minV = Math.min(...allValues);
  const maxV = Math.max(...allValues);
  const range = (maxV - minV) || 1; // avoid /0 on a perfectly flat series
  const pointsFor = (values) => {
    const arr = (values || []).map((v) => (Number.isFinite(Number(v)) ? Number(v) : null));
    const n = arr.length;
    if (n < 2) return '';
    return arr
      .map((v, i) => {
        if (v === null) return null;
        const x = padX + (i / (n - 1)) * innerW;
        const y = padY + innerH - ((v - minV) / range) * innerH;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .filter(Boolean)
      .join(' ');
  };
  return (
    <Box component="svg" width={width} height={height} sx={{ display: 'block', overflow: 'visible' }}>
      {series.map((s, idx) => {
        const pts = pointsFor(s.values);
        if (!pts) return null;
        return (
          <polyline
            key={idx}
            points={pts}
            fill="none"
            stroke={s.stroke}
            strokeWidth={s.strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={s.dashed ? '2 2' : undefined}
            opacity={s.opacity}
          />
        );
      })}
      {/* Highlight the most recent client point as a dot so the "you are here"
          marker is unambiguous. */}
      {(() => {
        const arr = (clientSeries || []).map((v) => (Number.isFinite(Number(v)) ? Number(v) : null));
        const lastIdx = arr.length - 1;
        const v = arr[lastIdx];
        if (v === null || v === undefined) return null;
        const x = padX + innerW;
        const y = padY + innerH - ((v - minV) / range) * innerH;
        return <circle cx={x} cy={y} r={2.2} fill={color} />;
      })()}
    </Box>
  );
}

function PeerFunnelComparison({ verticalName, clientStats = {}, cohort = null, onExtendWindow = null, narrative = undefined, narrativeLoading = false, trends = undefined, trendsLoading = false }) {
  // Stage-level rates for the client (each stage's standalone conversion).
  const clientRates = useMemo(() => ({
    set_rate: safeDiv(clientStats.booked, clientStats.leads),
    issue_rate: safeDiv(clientStats.issues, clientStats.scheduled),
    demo_rate: safeDiv(clientStats.demos, clientStats.issues),
    net_close_rate: safeDiv(clientStats.netSold, clientStats.demos),
    self_book_rate: safeDiv(clientStats.selfBook, clientStats.leads),
    // Compound (lead-to-X) rates. These share the same denominator (leads)
    // at every stage, so they're directly comparable across clients with
    // different booking criteria — unlike stage rates, which conflate
    // "performance at this stage" with "what got into this stage."
    lead_to_booked_rate: safeDiv(clientStats.booked, clientStats.leads),
    lead_to_issue_rate: safeDiv(clientStats.issues, clientStats.leads),
    lead_to_demo_rate: safeDiv(clientStats.demos, clientStats.leads),
    lead_to_sale_rate: safeDiv(clientStats.netSold, clientStats.leads),
    avg_ticket: safeDiv(clientStats.netAmount, clientStats.netSold),
    revenue_per_lead: safeDiv(clientStats.netAmount, clientStats.leads),
    cost_of_marketing: safeDiv(clientStats.spend, clientStats.netAmount),
  }), [clientStats]);

  // Wilson 95% CI per funnel-stage rate, keyed by rate_key. The denominator
  // determines how reliable each rate is — set_rate uses leads (big),
  // close_rate uses demos (often tiny). A small-volume client may have a
  // perfectly stable set rate but a wildly noisy close rate.
  const clientCIs = useMemo(() => ({
    set_rate: wilsonInterval(clientStats.booked, clientStats.leads),
    issue_rate: wilsonInterval(clientStats.issues, clientStats.scheduled),
    demo_rate: wilsonInterval(clientStats.demos, clientStats.issues),
    net_close_rate: wilsonInterval(clientStats.netSold, clientStats.demos),
    // Compound-rate CIs use the leads denominator at every stage.
    lead_to_booked_rate: wilsonInterval(clientStats.booked, clientStats.leads),
    lead_to_issue_rate: wilsonInterval(clientStats.issues, clientStats.leads),
    lead_to_demo_rate: wilsonInterval(clientStats.demos, clientStats.leads),
    lead_to_sale_rate: wilsonInterval(clientStats.netSold, clientStats.leads),
  }), [clientStats]);

  // If ANY stage CI is wider than this, the comparison is noisy enough to
  // warrant the small-sample banner.
  const NOISY_CI_THRESHOLD = 0.05; // 5 percentage points half-width
  const sampleTooSmall = useMemo(() => {
    return STAGE_TRANSITIONS.some((t) => {
      const ci = clientCIs[t.rateKey];
      return ci && ci.margin > NOISY_CI_THRESHOLD;
    });
  }, [clientCIs]);

  // Detect "winning" mode: client beats the vertical on 3+ of 4 funnel stages
  // STATISTICALLY SIGNIFICANTLY. Scored on COMPOUND (lead-to-X) rates for the
  // same reason the biggest-opportunity logic is — stage rates aren't a fair
  // cross-client comparison, so a client could "win" on stage rates while
  // their actual lead-to-revenue funnel is worse than peers.
  const winSummary = useMemo(() => {
    if (!cohort) return { winning: false, winCount: 0, totalScored: 0, avgPercentile: null };
    let wins = 0;
    let scored = 0;
    let percentileSum = 0;
    let percentileN = 0;
    STAGE_TRANSITIONS.forEach((t) => {
      const you = clientRates[t.compoundKey];
      const avg = Number(cohort[t.compoundKey]);
      const ci = clientCIs[t.compoundKey];
      if (Number.isFinite(you) && Number.isFinite(avg) && ci) {
        scored += 1;
        const significant = isSignificantGap(ci, avg);
        if (significant && you > avg) wins += 1;
      }
      const pct = Number(cohort[`${t.rateKey}_client_percentile`]);
      if (Number.isFinite(pct)) {
        percentileSum += pct;
        percentileN += 1;
      }
    });
    const avgPercentile = percentileN > 0 ? Math.round(percentileSum / percentileN) : null;
    // Celebration mode requires BOTH (a) winning 3+ stages significantly AND
    // (b) sitting in the top 40% of the distribution. The latter prevents
    // median performers who happen to beat the mean on a few stages from
    // getting "You're crushing it" framing when their actual percentile is 50.
    return {
      winning:
        scored >= 3
        && wins / scored >= 0.75
        && avgPercentile !== null
        && avgPercentile >= 60,
      winCount: wins,
      totalScored: scored,
      avgPercentile,
    };
  }, [cohort, clientRates, clientCIs]);

  // Compound % of leads reaching each stage, for both YOU and PEER.
  // The PEER reference is the vertical average by default, but flips to top
  // quartile in celebration mode so winners still have somewhere to aim.
  //
  // For the YOU side, we also propagate per-stage Wilson CIs through the
  // product: var(log compound) = sum of var(log each rate). This gives a
  // properly compounded uncertainty band — the Demos bar inherits noise from
  // all of set/issue/demo rates upstream, so its band is wider than Booked's.
  const compoundPcts = useMemo(() => {
    const refSuffix = winSummary.winning ? '_p75' : '';
    const peerRate = (key) => {
      const v = Number(cohort?.[`${key}${refSuffix}`]);
      return Number.isFinite(v) ? v : null;
    };
    const you = { leads: 1 };
    const peer = { leads: 1 };
    const youCI = { leads: { lower: 1, upper: 1 } };
    let yProd = 1;
    let pProd = 1;
    let logVarSum = 0; // accumulated variance of log(compound) for YOU
    STAGE_TRANSITIONS.forEach((t) => {
      const yRate = clientRates[t.rateKey];
      const pRate = peerRate(t.rateKey);
      yProd = Number.isFinite(yRate) ? yProd * yRate : null;
      pProd = pRate !== null && pProd !== null ? pProd * pRate : null;
      you[t.toKey] = yProd;
      peer[t.toKey] = pProd;

      // Compound CI propagation for YOU.
      const ci = clientCIs[t.rateKey];
      if (ci && Number.isFinite(yRate) && yRate > 0 && yProd !== null) {
        // Approximate σ(log p) ≈ (margin / 1.96) / p (rough delta-method conversion
        // from 95% margin back to one σ in log space). Adding variances across
        // independent stages gives the compound log-variance.
        const sigmaLog = (ci.margin / Z_95) / yRate;
        logVarSum += sigmaLog * sigmaLog;
        const logMargin = Z_95 * Math.sqrt(logVarSum);
        const logCompound = Math.log(yProd);
        youCI[t.toKey] = {
          lower: Math.max(0, Math.exp(logCompound - logMargin)),
          upper: Math.min(1, Math.exp(logCompound + logMargin)),
        };
      } else {
        youCI[t.toKey] = null;
      }
    });
    return { you, peer, youCI };
  }, [cohort, clientRates, clientCIs, winSummary.winning]);

  // Dollar headline: at peer-average rates × your actual leads, how many more
  // sales / how much more revenue would you have generated?
  const dollarHeadline = useMemo(() => {
    if (!cohort) return null;
    const leads = Number(clientStats.leads || 0);
    if (leads <= 0) return null;
    const actualSales = Number(clientStats.netSold || 0);
    const actualRevenue = Number(clientStats.netAmount || 0);
    const r = (key) => Number(cohort[key]);
    const set = r('set_rate'), issue = r('issue_rate'), demo = r('demo_rate'), close = r('net_close_rate');
    if (![set, issue, demo, close].every(Number.isFinite)) return null;
    const projectedSales = leads * set * issue * demo * close;
    const ticket = Number.isFinite(Number(cohort.avg_ticket))
      ? Number(cohort.avg_ticket)
      : safeDiv(actualRevenue, actualSales);
    const projectedRevenue = Number.isFinite(ticket) ? projectedSales * ticket : null;
    return {
      salesGap: Math.round(projectedSales - actualSales),
      revenueGap: projectedRevenue !== null ? Math.round(projectedRevenue - actualRevenue) : null,
    };
  }, [cohort, clientStats]);

  // Only call something a "biggest opportunity" if the gap is statistically
  // significant — otherwise we'd be coaching against noise.
  //
  // Diagnoses on COMPOUND (lead-to-X) rates, not stage rates. Stage rates
  // aren't comparable across clients because each defines what counts as
  // booked/scheduled differently — a client with high Set Rate + low Issue
  // Rate may have the same Lead-to-Issue as peers (i.e., no real issue-stage
  // problem, just looser booking). The compound rate is the honest diagnosis
  // of where in the funnel a client is actually losing leads vs peers. The
  // displayed stage rate is kept for context but the gap math is compound.
  const biggestOpportunity = useMemo(() => {
    if (!cohort || winSummary.winning) return null;
    let worst = null;
    STAGE_TRANSITIONS.forEach((t) => {
      const youCompound = clientRates[t.compoundKey];
      const avgCompound = Number(cohort[t.compoundKey]);
      const ci = clientCIs[t.compoundKey];
      if (Number.isFinite(youCompound) && Number.isFinite(avgCompound) && ci) {
        const delta = youCompound - avgCompound;
        const significant = isSignificantGap(ci, avgCompound);
        if (significant && delta < 0 && (!worst || delta < worst.delta)) {
          // Surface the stage rate for context (so the callout still says
          // "Issue Rate" / "Close Rate" — terms the client recognizes) but
          // the gap measurement itself comes from the compound rate.
          worst = {
            ...t,
            you: clientRates[t.rateKey],
            avg: Number(cohort[t.rateKey]),
            youCompound,
            avgCompound,
            delta,
          };
        }
      }
    });
    return worst;
  }, [cohort, clientRates, clientCIs, winSummary.winning]);

  // COM decomposition: when client's COM is above vertical avg, simulate the
  // impact of bringing each lever up to vertical-average performance.
  //
  // Math: COM = spend / net_amount. If we improve a stage rate from
  // client_rate → peer_rate, net_amount scales by (peer_rate/client_rate),
  // so new_COM = current_COM × (client_rate/peer_rate). Same formula for
  // avg_ticket since it's also a multiplicative driver of net_amount.
  const comDecomposition = useMemo(() => {
    if (!cohort) return null;
    const clientCOM = clientRates.cost_of_marketing;
    const peerCOM = Number(cohort.cost_of_marketing);
    if (!Number.isFinite(clientCOM) || !Number.isFinite(peerCOM)) return null;
    if (clientCOM <= peerCOM + 0.005) return null; // No meaningful gap — hide

    const buildLever = (rateKey, label, isCurrency = false) => {
      const clientRate = clientRates[rateKey];
      const peerRate = Number(cohort[rateKey]);
      if (!Number.isFinite(clientRate) || !Number.isFinite(peerRate)) return null;
      if (clientRate >= peerRate) {
        return { rateKey, label, isCurrency, clientRate, peerRate, alreadyAhead: true };
      }
      const newCOM = clientCOM * (clientRate / peerRate);
      return {
        rateKey,
        label,
        isCurrency,
        clientRate,
        peerRate,
        newCOM,
        deltaPt: newCOM - clientCOM, // negative = improvement
        alreadyAhead: false,
      };
    };

    const allLevers = [
      buildLever('set_rate', 'Set Rate'),
      buildLever('issue_rate', 'Issue Rate'),
      buildLever('demo_rate', 'Demo Rate'),
      buildLever('net_close_rate', 'Close Rate'),
      buildLever('avg_ticket', 'Avg Ticket', true),
    ].filter(Boolean);

    const opportunityLevers = allLevers
      .filter((l) => !l.alreadyAhead)
      .sort((a, b) => a.deltaPt - b.deltaPt); // most-improvement first

    // Combined: close ALL underperforming gaps at once. Multiplicative.
    let combinedMultiplier = 1;
    opportunityLevers.forEach((l) => {
      combinedMultiplier *= (l.clientRate / l.peerRate);
    });
    const combinedCOM = clientCOM * combinedMultiplier;

    return {
      clientCOM,
      peerCOM,
      gap: clientCOM - peerCOM,
      opportunityLevers,
      strengthLevers: allLevers.filter((l) => l.alreadyAhead),
      combinedCOM,
    };
  }, [cohort, clientRates]);

  // What's the sales lift if just the biggest-opportunity stage is closed to avg?
  const opportunityLift = useMemo(() => {
    if (!biggestOpportunity || !cohort) return null;
    const leads = Number(clientStats.leads || 0);
    if (leads <= 0) return null;
    const rateFor = (key) => {
      if (key === biggestOpportunity.rateKey) {
        const v = Number(cohort[key]);
        return Number.isFinite(v) ? v : (clientRates[key] || 0);
      }
      return Number(clientRates[key]) || 0;
    };
    const projected = leads
      * rateFor('set_rate')
      * rateFor('issue_rate')
      * rateFor('demo_rate')
      * rateFor('net_close_rate');
    const actual = Number(clientStats.netSold || 0);
    return Math.max(projected - actual, 0);
  }, [biggestOpportunity, cohort, clientStats, clientRates]);

  // M4: Pick the largest favorable market-wide shift (tailwind only) from the
  // vertical's prior-period comparison. Headwinds are intentionally hidden —
  // surfacing "vertical lead volume is down 33%" on a client dashboard reads
  // as excuse-making and demoralizes the client. When the market is genuinely
  // helping, we still surface it as a free win.
  const marketContextCallout = useMemo(() => {
    const mc = cohort?.market_context;
    if (!mc) return null;
    const candidates = [
      { key: 'cpl', label: 'cost per lead', delta: mc.cpl_change_pct, badDirection: 'higher' },
      { key: 'com', label: 'Cost of Marketing', delta: mc.com_change_pct, badDirection: 'higher' },
      { key: 'close_rate', label: 'close rates', delta: mc.close_rate_change_pct, badDirection: 'lower' },
      { key: 'avg_ticket', label: 'average deal size', delta: mc.avg_ticket_change_pct, badDirection: 'lower' },
      { key: 'lead_volume', label: 'lead volume', delta: mc.lead_volume_change_pct, badDirection: 'lower' },
    ];
    const tailwinds = candidates.filter((c) => {
      const d = Number(c.delta);
      if (!Number.isFinite(d) || Math.abs(d) < 0.05) return false;
      const isUp = d > 0;
      const isFavorable = (c.badDirection === 'higher' && !isUp)
        || (c.badDirection === 'lower' && isUp);
      return isFavorable;
    });
    if (!tailwinds.length) return null;
    tailwinds.sort((a, b) => Math.abs(Number(b.delta)) - Math.abs(Number(a.delta)));
    return tailwinds[0];
  }, [cohort?.market_context]);

  if (!cohort) return null;

  const clientCount = Number(cohort.client_count || 0);
  const reportingCount = Number(cohort.reporting_client_count || 0);
  const isWinning = winSummary.winning;
  const peerHeaderLabel = isWinning ? 'Top Quartile' : 'Vertical Average';

  // Largest compound % across either side — used to rescale the funnel bars
  // so the widest stage fills the half-width and the funnel actually looks
  // like a funnel instead of small bars stranded in white space. Include
  // upper CI bounds so the wiggle bands don't get clipped on the widest stage.
  let maxCompound = 0;
  FUNNEL_STAGES.forEach((stage) => {
    const y = compoundPcts.you[stage.key];
    const p = compoundPcts.peer[stage.key];
    const yU = compoundPcts.youCI[stage.key]?.upper;
    if (Number.isFinite(y) && y > maxCompound) maxCompound = y;
    if (Number.isFinite(p) && p > maxCompound) maxCompound = p;
    if (Number.isFinite(yU) && yU > maxCompound) maxCompound = yU;
  });
  if (maxCompound <= 0) maxCompound = 1;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        background: isWinning
          ? 'linear-gradient(180deg, rgba(34, 197, 94, 0.05), rgba(34, 197, 94, 0.01))'
          : 'linear-gradient(180deg, rgba(99, 102, 241, 0.05), rgba(99, 102, 241, 0.01))',
        border: '1px solid',
        borderColor: isWinning ? 'rgba(34, 197, 94, 0.22)' : 'rgba(99, 102, 241, 0.18)',
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        {isWinning ? (
          <CelebrationOutlinedIcon sx={{ color: '#16a34a', fontSize: 24 }} />
        ) : (
          <EmojiEventsOutlinedIcon sx={{ color: '#6366f1', fontSize: 24 }} />
        )}
        <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.1rem' }}>
          {isWinning
            ? (sampleTooSmall
                ? `Trending strong in ${verticalName}`
                : `You're crushing it in ${verticalName}`)
            : `How You Stack Up — ${verticalName}`}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
        {isWinning && winSummary.avgPercentile !== null ? (
          <>
            {sampleTooSmall ? (
              <>
                On this sample, you're tracking above vertical average on {winSummary.winCount} of {winSummary.totalScored} funnel stages.{' '}
                <Box component="span" sx={{ fontStyle: 'italic' }}>
                  Sample sizes are small, so treat this as a positive signal — not a guarantee future months will look the same.
                </Box>
                {' '}Comparing against top-quartile performers below.
              </>
            ) : (
              <>
                You're outperforming approximately{' '}
                <Box component="span" sx={{ fontWeight: 700, color: '#16a34a' }}>
                  {winSummary.avgPercentile}% of Lead Guru clients
                </Box>
                {' '}in {verticalName} ({winSummary.winCount} of {winSummary.totalScored} funnel stages above vertical average). Comparing against top-quartile performers below.
              </>
            )}
          </>
        ) : (
          <>
            Comparing your funnel to{' '}
            <Tooltip
              arrow
              title="Averaged across other Lead Guru clients in your vertical with backend reporting. Each rate is computed only over clients reporting that funnel step — missing-data clients are excluded per metric."
            >
              <Box component="span" sx={{ fontWeight: 700, color: '#4f46e5', borderBottom: '1px dotted', cursor: 'help' }}>
                other Lead Guru clients in your vertical
              </Box>
            </Tooltip>
            {' '}for the same period. Bars show the percentage of leads that reach each stage.
          </>
        )}
      </Typography>

      {/* AI-generated narrative summary — 2-3 sentence interpretation of the
          benchmark data, sourced from Claude. Renders a sparkle-icon card so
          it reads as "expert insight" not "robot output." */}
      {(narrativeLoading || (narrative && typeof narrative === 'string' && narrative.length > 0)) && (
        <Box
          sx={{
            mb: 2,
            p: 1.75,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.08), rgba(99, 102, 241, 0.04))',
            border: '1px solid rgba(168, 85, 247, 0.22)',
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <AutoAwesomeIcon sx={{ color: '#a855f7', fontSize: 20, mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.66rem',
                  color: '#7c3aed',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Lead Guru Insight
              </Typography>
              {narrativeLoading ? (
                <Stack spacing={0.5}>
                  <Skeleton variant="text" width="92%" height={16} />
                  <Skeleton variant="text" width="86%" height={16} />
                  <Skeleton variant="text" width="60%" height={16} />
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ fontSize: '0.88rem', lineHeight: 1.5, color: 'text.primary' }}>
                  {narrative}
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      )}

      {/* M4: Market tailwind — only renders when the vertical-wide shift is
          favorable. Headwinds are intentionally hidden to avoid client-facing
          negativity; the dashboard isn't the place to surface industry pain. */}
      {marketContextCallout && (() => {
        const delta = Number(marketContextCallout.delta);
        const isUp = delta > 0;
        const arrow = isUp ? '↑' : '↓';
        const pctText = `${Math.abs(delta * 100).toFixed(0)}%`;
        const directionWord = isUp ? 'up' : 'down';
        return (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(34, 197, 94, 0.06)', border: '1px solid rgba(34, 197, 94, 0.22)' }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box component="span" sx={{ fontSize: '1rem', mt: 0.1 }}>🌐</Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, fontSize: '0.66rem', color: '#16a34a', letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', mb: 0.25 }}
                >
                  Industry Tailwind
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.85rem', lineHeight: 1.45 }}>
                  {verticalName} {marketContextCallout.label} is{' '}
                  <Box component="span" sx={{ fontWeight: 800, color: '#16a34a' }}>
                    {arrow} {pctText} {directionWord}
                  </Box>
                  {' '}this period vs the prior period across the vertical. Momentum is broadly favorable.
                </Typography>
              </Box>
            </Stack>
          </Box>
        );
      })()}

      {/* Dollar-impact headline */}
      {dollarHeadline && Number.isFinite(dollarHeadline.salesGap) && (
        <Box
          sx={{
            mb: 2,
            p: 1.75,
            borderRadius: 2,
            background: isWinning
              ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(34, 197, 94, 0.04))'
              : 'linear-gradient(135deg, rgba(99, 102, 241, 0.12), rgba(99, 102, 241, 0.04))',
            border: '1px solid',
            borderColor: isWinning ? 'rgba(34, 197, 94, 0.25)' : 'rgba(99, 102, 241, 0.25)',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
            {sampleTooSmall && (
              <Box component="span" sx={{ fontWeight: 700, color: '#1e40af', mr: 0.5 }}>
                On this sample,
              </Box>
            )}
            {isWinning ? (
              // salesGap is computed as (peer-projected − actual). When winning
              // we're above peer-projected, so the raw value is negative — flip
              // the sign so the headline reads as "+X more sales than peer avg".
              <>
                You generated{' '}
                <Box component="span" sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#16a34a' }}>
                  +{fmtInt(-dollarHeadline.salesGap)} more sales
                </Box>
                {dollarHeadline.revenueGap !== null && (
                  <>
                    {' / '}
                    <Box component="span" sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#16a34a' }}>
                      +{fmtMoneyShort(-dollarHeadline.revenueGap)} revenue
                    </Box>
                  </>
                )}
                {' '}than the vertical average would have produced from your {fmtInt(clientStats.leads)} leads this period.
              </>
            ) : dollarHeadline.salesGap >= 0 ? (
              <>
                At vertical-average rates, your{' '}
                <Box component="span" sx={{ fontWeight: 800 }}>{fmtInt(clientStats.leads)} leads</Box>{' '}
                would have generated{' '}
                <Box component="span" sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#4f46e5' }}>
                  +{fmtInt(dollarHeadline.salesGap)} more sales
                </Box>
                {dollarHeadline.revenueGap !== null && (
                  <>
                    {' / '}
                    <Box component="span" sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#4f46e5' }}>
                      +{fmtMoneyShort(dollarHeadline.revenueGap)} revenue
                    </Box>
                  </>
                )}
                {' '}this period.
              </>
            ) : (
              // Client is actually ahead of vertical avg, but not significantly
              // enough to trigger celebration mode. Reword so the language
              // matches the direction of the gap.
              <>
                You generated{' '}
                <Box component="span" sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#4f46e5' }}>
                  +{fmtInt(-dollarHeadline.salesGap)} more sales
                </Box>
                {dollarHeadline.revenueGap !== null && (
                  <>
                    {' / '}
                    <Box component="span" sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#4f46e5' }}>
                      +{fmtMoneyShort(-dollarHeadline.revenueGap)} revenue
                    </Box>
                  </>
                )}
                {' '}than vertical-average rates would have produced from your {fmtInt(clientStats.leads)} leads this period.
              </>
            )}
          </Typography>
        </Box>
      )}

      {/* Small-sample advisory: surfaces when any stage CI is too wide */}
      {sampleTooSmall && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.25}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(59, 130, 246, 0.06)',
            border: '1px solid rgba(59, 130, 246, 0.22)',
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e40af' }}>
              Heads up: small sample — rates shown could shift in either direction with more data.
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
              Each rate is labeled with its likely range. A short-period spike to 30% could just as easily be 18% next month, and a temporary dip to 14% could really be 22% — natural sampling variation works both ways. We've marked any peer comparison that falls within your range as "noise" rather than a win or loss. Extend to 90 days for a more reliable read.
            </Typography>
          </Box>
          {onExtendWindow && (
            <Box
              component="button"
              onClick={onExtendWindow}
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                border: '1px solid #3b82f6',
                bgcolor: '#3b82f6',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.78rem',
                cursor: 'pointer',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#2563eb' },
              }}
            >
              Extend to 90 days
            </Box>
          )}
        </Stack>
      )}

      {/* Coaching banner: biggest opportunity */}
      {biggestOpportunity && (
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="flex-start"
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(245, 158, 11, 0.08)',
            border: '1px solid rgba(245, 158, 11, 0.25)',
          }}
        >
          <LightbulbOutlinedIcon sx={{ color: '#d97706', fontSize: 22, mt: 0.25 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#92400e' }}>
              Your biggest opportunity: {biggestOpportunity.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
              Your {biggestOpportunity.compoundLabel} rate is {fmtPct(biggestOpportunity.youCompound)} vs vertical {fmtPct(biggestOpportunity.avgCompound)}
              {' '}({biggestOpportunity.delta > 0 ? '+' : ''}{(biggestOpportunity.delta * 100).toFixed(1)}pt).
              {opportunityLift !== null && opportunityLift > 0 && (
                <> Closing this gap alone would mean{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: '#92400e' }}>
                    ~{fmtInt(opportunityLift)} more sales
                  </Box>
                  {' '}this period.
                </>
              )}
            </Typography>
          </Box>
        </Stack>
      )}

      {/* Funnel headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 130px 1fr', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="overline" sx={{ textAlign: 'right', color: '#4f46e5', fontWeight: 800, letterSpacing: '0.08em', fontSize: '0.7rem' }}>
          You
        </Typography>
        <Box />
        <Typography variant="overline" sx={{ textAlign: 'left', color: isWinning ? '#16a34a' : 'text.secondary', fontWeight: 800, letterSpacing: '0.08em', fontSize: '0.7rem' }}>
          {peerHeaderLabel}
        </Typography>
      </Box>

      {/* Anchor line so the percentages have an obvious referent */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textAlign: 'center',
          color: 'text.disabled',
          fontStyle: 'italic',
          fontSize: '0.72rem',
          mb: 1,
        }}
      >
        Of every 100 leads, here's what happens:
      </Typography>

      {/* Funnel: each stage prefixed by the conversion rate that produces it */}
      <Stack spacing={0}>
        {FUNNEL_STAGES.map((stage, idx) => {
          const transition = STAGE_TRANSITIONS[idx]; // rate that converts the previous stage into this one
          const youPct = compoundPcts.you[stage.key];
          const peerPct = compoundPcts.peer[stage.key];
          const youRate = clientRates[transition.rateKey];
          const refKey = isWinning ? `${transition.rateKey}_p75` : transition.rateKey;
          const peerRate = Number(cohort[refKey]);
          const peerRateValid = Number.isFinite(peerRate);
          const rateDelta = (Number.isFinite(youRate) && peerRateValid) ? youRate - peerRate : null;
          const ci = clientCIs[transition.rateKey];
          const significant = ci && peerRateValid ? isSignificantGap(ci, peerRate) : false;
          const ciMarginPct = ci ? (ci.margin * 100).toFixed(1) : null;
          // When the CI is wide, show the plausible range inline so the rate
          // value itself reads as uncertain. Applies in BOTH directions — a
          // small-sample 30% close rate is just as suspect as a small-sample
          // 2% close rate.
          const noisyRate = ci && ci.margin > 0.03;
          const ciRangeLabel = ci ? `${fmtPct(ci.lower)}–${fmtPct(ci.upper)}` : null;
          return (
            <React.Fragment key={stage.key}>
              {/* Conversion rate row — what % converted from the previous stage */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 130px 1fr', alignItems: 'center', py: 0.55 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pr: 1.25, gap: 0.5, flexWrap: 'wrap' }}>
                  {trends?.client?.[transition.rateKey] && trends?.vertical_avg?.[transition.rateKey] && (
                    <Tooltip
                      arrow
                      title={(
                        <Box sx={{ maxWidth: 280, fontSize: '0.78rem' }}>
                          <Box sx={{ fontWeight: 700, mb: 0.5 }}>6-month {transition.label.toLowerCase()} trend</Box>
                          <Box sx={{ lineHeight: 1.4 }}>
                            Solid line is your monthly rate; dashed is the vertical average across peers. The dot marks the most recent month. Use this to see if you're catching up, holding, or falling behind over time.
                          </Box>
                        </Box>
                      )}
                    >
                      <Box sx={{ display: 'inline-flex', cursor: 'help' }}>
                        <MiniSparkline
                          clientSeries={trends.client[transition.rateKey]}
                          peerSeries={trends.vertical_avg[transition.rateKey]}
                          color={stage.color}
                        />
                      </Box>
                    </Tooltip>
                  )}
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                    ↓ {transition.label} {fmtPct(youRate)}
                  </Typography>
                  {ci && (
                    <Tooltip
                      arrow
                      title={`Based on ${fmtInt(ci.n)} ${transition.label.toLowerCase()} samples. The true rate is 95% likely to fall between ${fmtPct(ci.lower)} and ${fmtPct(ci.upper)} — sample size alone could shift the displayed value several points in either direction.`}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: noisyRate ? '0.68rem' : '0.62rem',
                          color: noisyRate ? '#1e40af' : 'text.disabled',
                          fontWeight: noisyRate ? 700 : 400,
                          borderBottom: '1px dotted',
                          cursor: 'help',
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {noisyRate ? `likely ${ciRangeLabel}` : `±${ciMarginPct}pt`}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {rateDelta !== null && (
                    significant ? (
                      <DeltaPill delta={rateDelta} format="pt" goodDirection="higher" deadband={0.005} />
                    ) : (
                      <Tooltip arrow title="The peer rate falls within your statistical confidence band — this difference is consistent with normal sampling variation, not a real performance gap.">
                        <Stack direction="row" alignItems="center" spacing={0.3} sx={{ color: 'text.disabled', cursor: 'help' }}>
                          <RemoveIcon sx={{ fontSize: 12 }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.66rem', borderBottom: '1px dotted' }}>noise</Typography>
                        </Stack>
                      </Tooltip>
                    )
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', pl: 1.25 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                    ↓ {transition.label} {peerRateValid ? fmtPct(peerRate) : '—'}
                  </Typography>
                </Box>
              </Box>
              {/* Stage row: two half-bars + center label */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 130px 1fr', alignItems: 'center', py: 0.75 }}>
                <Box sx={{ pr: 1 }}>
                  <HalfBar
                    pctOfLeads={Number.isFinite(youPct) ? youPct : 0}
                    ciLower={compoundPcts.youCI[stage.key]?.lower}
                    ciUpper={compoundPcts.youCI[stage.key]?.upper}
                    scaleMax={maxCompound}
                    color={stage.color}
                    side="left"
                    label={Number.isFinite(youPct) ? fmtPct(youPct) : '—'}
                    tooltipTitle={(() => {
                      const youBand = compoundPcts.youCI[stage.key];
                      if (!Number.isFinite(youPct)) return null;
                      const hasBand = youBand && Number.isFinite(youBand.lower) && Number.isFinite(youBand.upper);
                      return (
                        <Box sx={{ maxWidth: 320 }}>
                          <Box sx={{ fontWeight: 700, mb: hasBand ? 0.75 : 0 }}>
                            Your {stage.label.toLowerCase()} rate: {fmtPct(youPct)} of leads reach this stage.
                          </Box>
                          {hasBand && (
                            <Box sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
                              The striped wiggle zone is your realistic range based on sample size — the true rate could fall anywhere between{' '}
                              <Box component="span" sx={{ fontWeight: 700 }}>{fmtPct(youBand.lower)}</Box>{' '}and{' '}
                              <Box component="span" sx={{ fontWeight: 700 }}>{fmtPct(youBand.upper)}</Box>.
                              Bands grow wider further down the funnel because uncertainty compounds across stages.
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary' }}
                  >
                    {stage.label}
                  </Typography>
                </Box>
                <Box sx={{ pl: 1 }}>
                  <HalfBar
                    pctOfLeads={Number.isFinite(peerPct) ? peerPct : 0}
                    scaleMax={maxCompound}
                    color={stage.color}
                    side="right"
                    dim
                    label={Number.isFinite(peerPct) ? fmtPct(peerPct) : '—'}
                    tooltipTitle={Number.isFinite(peerPct) ? (
                      <Box sx={{ maxWidth: 280 }}>
                        <Box sx={{ fontWeight: 700, mb: 0.5 }}>
                          {peerHeaderLabel}: {fmtPct(peerPct)} of leads reach {stage.label.toLowerCase()}.
                        </Box>
                        <Box sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
                          Averaged across other Lead Guru clients in your {verticalName} vertical for the same period. Used as your reference point.
                        </Box>
                      </Box>
                    ) : null}
                  />
                </Box>
              </Box>
            </React.Fragment>
          );
        })}
      </Stack>

      {/* COM decomposition: what each lever would do if it matched vertical avg */}
      {comDecomposition && (
        <Box
          sx={{
            mt: 2.5,
            pt: 2,
            borderTop: '1px dashed',
            borderColor: isWinning ? 'rgba(34, 197, 94, 0.2)' : 'rgba(99, 102, 241, 0.2)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
            <Box component="span" sx={{ fontSize: '1rem' }}>💸</Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.92rem' }}>
              What's driving your Cost of Marketing
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem', mb: 1.25 }}>
            Your COM of{' '}
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{fmtPct(comDecomposition.clientCOM)}</Box>
            {' '}is{' '}
            <Box component="span" sx={{ fontWeight: 700, color: '#dc2626' }}>
              +{(comDecomposition.gap * 100).toFixed(1)}pt above
            </Box>
            {' '}the vertical average of{' '}
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{fmtPct(comDecomposition.peerCOM)}</Box>.
            {' '}Here's what each lever would do if you brought it up to vertical-average performance:
          </Typography>
          <Stack spacing={0.5}>
            {comDecomposition.opportunityLevers.map((l, idx) => (
              <Box
                key={l.rateKey}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1.5fr 2fr 1.5fr' },
                  alignItems: 'center',
                  gap: { xs: 0.5, md: 1.5 },
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  bgcolor: idx === 0 ? 'rgba(245, 158, 11, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                  border: idx === 0 ? '1px solid rgba(245, 158, 11, 0.25)' : '1px solid transparent',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  {idx === 0 && <Box component="span" sx={{ color: '#d97706', fontSize: '0.85rem' }}>★</Box>}
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.82rem', color: idx === 0 ? '#92400e' : 'text.primary' }}>
                    {l.label}
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  {l.isCurrency ? fmtMoney(l.clientRate) : fmtPct(l.clientRate)}
                  {' → '}
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {l.isCurrency ? fmtMoney(l.peerRate) : fmtPct(l.peerRate)}
                  </Box>
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    COM →
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#16a34a' }}>
                    {fmtPct(l.newCOM)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#16a34a' }}>
                    ({(l.deltaPt * 100).toFixed(1)}pt)
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Stack>
          {comDecomposition.opportunityLevers.length > 1 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontSize: '0.78rem', fontStyle: 'italic' }}>
              Closing every gap above would bring your COM to{' '}
              <Box component="span" sx={{ fontWeight: 800, color: '#16a34a' }}>~{fmtPct(comDecomposition.combinedCOM)}</Box>
              {' '}— matching what top-quartile operators in your vertical achieve.
            </Typography>
          )}
          {comDecomposition.strengthLevers.length > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#16a34a', fontSize: '0.75rem' }}>
              Already at or above vertical average on:{' '}
              {comDecomposition.strengthLevers.map((l) => l.label).join(', ')}.
            </Typography>
          )}
        </Box>
      )}

      {/* Bottom summary stats */}
      <Box
        sx={{
          mt: 2.5,
          pt: 2,
          borderTop: '1px dashed',
          borderColor: isWinning ? 'rgba(34, 197, 94, 0.2)' : 'rgba(99, 102, 241, 0.2)',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 1.5,
        }}
      >
        {BOTTOM_TILES.map((tile) => {
          const peerVal = Number.isFinite(Number(cohort[tile.key])) ? Number(cohort[tile.key]) : null;
          // Top-quartile = best performers. For higher-is-better metrics that's p75;
          // for lower-is-better (COM) it's p25.
          const direction = tile.goodDirection || 'higher';
          const topQKey = direction === 'lower' ? `${tile.key}_p25` : `${tile.key}_p75`;
          const topQ = tile.showTopQuartile && Number.isFinite(Number(cohort[topQKey]))
            ? Number(cohort[topQKey])
            : null;
          const tooltipBody = tile.key === 'cost_of_marketing' ? (
            <Box sx={{ maxWidth: 320 }}>
              <Box sx={{ fontWeight: 700, mb: 0.5 }}>Cost of Marketing (COM)</Box>
              <Box sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
                What you spend on marketing as a percentage of revenue generated. Lower is better.
                The top quartile shows what the most efficient operators in your vertical achieve —
                that's the level we work to get you to.
              </Box>
            </Box>
          ) : null;
          return (
            <StatTile
              key={tile.key}
              label={tile.label}
              icon={tile.icon}
              youValue={clientRates[tile.key]}
              peerValue={peerVal}
              topQuartileValue={topQ}
              format={tile.format}
              goodDirection={direction}
              tooltipBody={tooltipBody}
            />
          );
        })}
      </Box>
    </Paper>
  );
}

export default memo(PeerFunnelComparison);
